import { type KeyboardEvent } from 'react';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import {
    focusNextField,
    isSingleLineEntry,
    isSingleValueAutocomplete
} from './focusNextField';

/**
 * The fullscreen record editors' Dialog. Behaves like MUI's `Dialog` but opens
 * INSTANTLY (no enter/exit fade), saves on Ctrl/Cmd+Enter, and moves on to the
 * next field on a plain Enter.
 *
 * These editors show a shell ({@link FormDialogShell}) the moment they mount
 * and swap it for the filled form once the record loads — a fade would let the
 * grid page shine through during that swap (two separate Dialogs), and likewise
 * when one editor replaces another on a reference jump.
 */
export default function FormDialog({
    onKeyDown,
    onKeyDownCapture,
    ...props
}: DialogProps) {
    /**
     * A date or time field of MUI X submits the form on Enter by itself, ahead
     * of the rule below. So its Enter is taken here, in the capture phase, and
     * never reaches the field: Ctrl+Enter saves, a plain Enter moves on. (Its
     * own opt-out, `defaultMuiPrevented`, cannot be set from here — React
     * hands the capture and bubble phases separate event objects.)
     */
    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDownCapture?.(event);
        const target = event.target;
        if (
            event.key !== 'Enter' ||
            !(target instanceof HTMLElement) ||
            !target.closest('.MuiPickersSectionList-root')
        ) {
            return;
        }
        const form = target.closest('form');
        if (!form) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (event.ctrlKey || event.metaKey) {
            form.requestSubmit();
        } else if (!event.altKey && !event.shiftKey) {
            focusNextField(form, target);
        }
    };

    /**
     * Ctrl/Cmd+Enter submits the dialog's form. A plain Enter never does: in a
     * one-line field it means "done here" and moves to the next field, like in
     * a spreadsheet, and in a multiline field it is a line break. Saving by
     * Enter used to store what was half typed — "Ke" for the "Keramik" the
     * suggestions showed.
     *
     * `requestSubmit()` goes through the form's own submit handler, so
     * validation, the unsaved-changes guard and the save path behave exactly as
     * they do for the footer's Save button.
     */
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event);
        if (event.key !== 'Enter' || event.altKey || event.shiftKey) {
            return;
        }
        const form = event.currentTarget.querySelector('form');
        // React events bubble along the REACT tree, so a keystroke inside a
        // NESTED dialog (the unsaved-changes prompt) arrives here too, even
        // though it is portalled elsewhere in the DOM.
        if (!form || !form.contains(event.target as Node)) {
            return;
        }
        if (event.ctrlKey || event.metaKey) {
            if (!event.defaultPrevented) {
                event.preventDefault();
                form.requestSubmit();
            }
            return;
        }
        if (!isSingleLineEntry(event.target)) {
            return;
        }
        /**
         * A field that used the key itself stays — Enter added a chip to a
         * multi-value field, and the next one may follow. A single-value
         * Autocomplete used it too (it took the typed text or the picked
         * option), but it is done and the form moves on.
         */
        const target = event.target as HTMLElement;
        const keptByField =
            event.defaultPrevented && !isSingleValueAutocomplete(target);
        event.preventDefault();
        if (!keptByField) {
            focusNextField(form, target);
        }
    };

    return (
        <Dialog
            transitionDuration={0}
            onKeyDown={handleKeyDown}
            onKeyDownCapture={handleKeyDownCapture}
            {...props}
        />
    );
}
